import { DateTimeField } from '@iheartjane/reefer';
import type { DateTimeFieldProps } from '@iheartjane/reefer';

import type { FormFieldProps } from '../formField.types';
import { FormFieldWrapper } from '../formFieldWrapper/formFieldWrapper';

export interface FormDateTimeFieldProps
  extends
    Omit<DateTimeFieldProps, 'children'>,
    Omit<FormFieldProps, 'max' | 'maxLength' | 'min' | 'minLength'> {}

/**
 * DateTime fields allow users to specify the date, time or both values into a UI.
 *
 * Use this component *within forms* for inputs of `type`: `date`, `time`, or `datetime-local`.
 *
 * _Field validations supported:_ `required`, `validate`.
 *
 * **NOTE: DO NOT USE THIS COMPONENT OUTSIDE FORMS**
 *
 * For a similar component for use outside forms, see [`DateTimeField`](/story/components-forms-fields-datetimefield--default).
 */

export function FormDateTimeField({
  autoFocus = false,
  defaultValue,
  disabled = false,
  autocomplete,
  helperText,
  label,
  labelHidden = false,
  max,
  min,
  name,
  onBlur,
  onChange,
  readOnly = false,
  required = false,
  shouldUnregister,
  step,
  'data-testid': testId,
  type = 'date',
  validate,
  width,
  ...props
}: FormDateTimeFieldProps) {
  return (
    <FormFieldWrapper
      defaultValue={defaultValue}
      name={name}
      render={({ children, field, invalid }) => (
        <DateTimeField
          aria-invalid={invalid}
          autoFocus={autoFocus}
          defaultValue={defaultValue}
          disabled={disabled}
          autocomplete={autocomplete}
          helperText={helperText}
          label={label}
          labelHidden={labelHidden}
          name={name}
          onBlur={(value) => {
            onBlur && onBlur(value);
            field.onBlur();
          }}
          onChange={(value) => {
            onChange && onChange(value);
            field.onChange(value);
          }}
          onUpdate={onChange}
          min={min}
          max={max}
          readOnly={readOnly}
          required={required}
          step={step}
          ref={field.ref}
          type={type}
          data-testid={testId}
          value={field.value}
          width={width}
          {...props}
        >
          {children}
        </DateTimeField>
      )}
      required={required}
      shouldUnregister={shouldUnregister}
      validate={validate}
      {...(type === 'time'
        ? { minTime: min, maxTime: max }
        : { minDate: min, maxDate: max })}
    />
  );
}
